import { Account } from '@ndvr/portfolio-api';

import { AccountTaxMetric } from '@/cip/_openapi/portfolio-optimizer';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatCurrency } from 'utils';
import { cn } from 'utils/cn';
import { getEffectiveTaxBudget, getTaxBudgetUsage } from 'views/account/tax-budget';

const NEAR_LIMIT_RATIO = 0.9;

const PnlBreakdown = ({ metrics }: { metrics: AccountTaxMetric }) => (
    <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
            <div className="text-muted-foreground font-medium">Short-Term Realized P&L YTD</div>
            <div className="font-medium">{formatCurrency(metrics.ytdStPnl)}</div>
        </div>
        <div>
            <div className="text-muted-foreground font-medium">Long-Term Realized P&L YTD</div>
            <div className="font-medium">{formatCurrency(metrics.ytdLtPnl)}</div>
        </div>
    </div>
);

export const TaxBudgetUsage = ({
    account,
    metrics,
    isLoading,
}: {
    account: Account;
    metrics?: AccountTaxMetric;
    isLoading: boolean;
}) => {
    if (isLoading) {
        return <div className="bg-muted h-16 animate-pulse rounded-md" />;
    }
    if (metrics?.ytdTax == null) {
        return (
            <div className="text-muted-foreground text-sm">Realized tax YTD is not available for this account yet.</div>
        );
    }

    const ytdTax = metrics.ytdTax;
    const budget = getEffectiveTaxBudget(account);

    if (budget.kind !== 'amount') {
        return (
            <div className="space-y-3">
                <div>
                    <div className="text-muted-foreground text-sm font-medium">Realized Tax YTD</div>
                    <div className="flex items-baseline gap-2">
                        <div className="text-2xl font-semibold">{formatCurrency(ytdTax)}</div>
                        {budget.kind === 'unlimited' && <Badge variant="secondary">Unlimited budget</Badge>}
                    </div>
                </div>
                <PnlBreakdown metrics={metrics} />
            </div>
        );
    }

    const { consumed, overBudget, percent } = getTaxBudgetUsage(budget.amount, ytdTax);
    const nearLimit = consumed >= NEAR_LIMIT_RATIO * budget.amount;

    return (
        <div className="space-y-3">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="text-muted-foreground text-sm font-medium">Realized Tax YTD</div>
                    <div className="text-2xl font-semibold">
                        {formatCurrency(ytdTax)}{' '}
                        <span className="text-muted-foreground text-lg font-normal">
                            of {formatCurrency(budget.amount)}
                        </span>
                    </div>
                </div>
                <div className="text-muted-foreground pb-1 text-sm whitespace-nowrap">
                    {overBudget
                        ? `Budget exceeded by ${formatCurrency(ytdTax - budget.amount)}`
                        : `${percent}% of budget reached YTD`}
                </div>
            </div>
            <Progress
                value={percent}
                className={cn('h-2', nearLimit ? '[&>div]:bg-seafoam-500' : '[&>div]:bg-seafoam-300')}
            />
            <PnlBreakdown metrics={metrics} />
        </div>
    );
};
